import type { BlockContent } from '../../api/bindings/BlockContent.ts';

// BlockContent is a discriminated union on `kind`; these helpers extract one variant.
export type BlockKind = BlockContent['kind'];
export type ContentOf<K extends BlockKind> = Extract<BlockContent, { kind: K }>;

export interface ViewProps<K extends BlockKind> {
  content: ContentOf<K>;
}

export interface EditProps<K extends BlockKind> {
  content: ContentOf<K>;
  onChange: (content: ContentOf<K>) => void;
}

export const BLOCK_KIND_LABELS: Record<BlockKind, string> = {
  narrative: 'Narrative',
  metric_grid: 'Key metrics',
  canvas: 'Business Model Canvas',
  swot: 'SWOT',
  table: 'Table',
  risk_list: 'Risks',
  phase_list: 'Phases',
  checklist: 'Checklist',
  team_list: 'Team & partners',
  kv_list: 'Facts',
};

/** An empty payload for each kind — used when adding a new block. */
export function emptyContent(kind: BlockKind): BlockContent {
  switch (kind) {
    case 'narrative':
      return { kind, text: '' };
    case 'metric_grid':
      return { kind, items: [] };
    case 'canvas':
      return {
        kind,
        boxes: {
          key_partners: [],
          key_activities: [],
          value_proposition: [],
          customer_relationships: [],
          customer_segments: [],
          key_resources: [],
          channels: [],
          cost_structure: [],
          revenue_streams: [],
        },
      };
    case 'swot':
      return { kind, strengths: [], weaknesses: [], opportunities: [], threats: [] };
    case 'table':
      return { kind, columns: ['Column'], rows: [] };
    case 'risk_list':
      return { kind, items: [] };
    case 'phase_list':
      return { kind, phases: [] };
    case 'checklist':
      return { kind, groups: [] };
    case 'team_list':
      return { kind, members: [], partners: [] };
    case 'kv_list':
      return { kind, items: [] };
  }
}
